// src/api/courseApi.js
import apiClient from "./apiClient";

// 코스 리스트 조회
// 백엔드 Search 파라미터 구조에 맞춰서 쿼리스트링을 구성한다.
//  - search.keyword
//  - search.minDistance
//  - search.maxDistance
//  - search.onlyMyCourse
//  - userId
//  - currentLocationWkt  등 :contentReference[oaicite:3]{index=3}
export async function getCourseList({
  keyword,
  minDistance,
  maxDistance,
  onlyMyCourse,
  userId,
  currentLocationWkt,
} = {}) {
  const params = {};

  // 검색어: 공백 제거 후 값이 있으면만 전달
  if (keyword && keyword.trim() !== "") {
    params.keyword = keyword.trim();
  }

  // 최소 / 최대 거리: 빈문자열이 아니라면 그대로 전달
  if (minDistance !== undefined && minDistance !== null && minDistance !== "") {
    params.minDistance = minDistance;
  }
  if (maxDistance !== undefined && maxDistance !== null && maxDistance !== "") {
    params.maxDistance = maxDistance;
  }

  // 내 코스만: true 일 때만 전달 (false/null은 안 보냄)
  if (onlyMyCourse) {
    params.onlyMyCourse = true;
  }

  if (userId) {
    params.userId = userId;
  }

  if (currentLocationWkt) {
    params.currentLocationWkt = currentLocationWkt;
  }

  const res = await apiClient.get("/course/listCourse", { params });
  return res.data; // List<Course>
}

// 인기 코스(평균 별점 상위 5개) + 대표후기 + 후기수 조회
// - GET /course/listTopRated
export async function listTopRatedCoursesForHome() {
  const res = await apiClient.get("/course/listTopRated");
  return res.data; // [{ course, representativeReview, reviewCount }, ...]
}

// 코스 상세 조회
// GET /course/getCourse?courseId=... :contentReference[oaicite:4]{index=4}
export async function getCourseDetail(courseId) {
  const res = await apiClient.get("/course/getCourse", {
    params: { courseId },
  });
  return res.data; // Course
}

// 2) 코스 공개 전환
export const publicCourse = async (courseId, userId) => {
  const response = await apiClient.post(
    "/course/publicCourse",
    null, // body 없음
    {
      params: { courseId, userId },
    }
  );
  return response.data;
};

// 코스 사용 시작
export async function useCourse(courseId, userId, battleId) {
  const res = await apiClient.get("/course/useCourse", {
    params: {
      courseId,
      userId,
      battleId,
    },
  });
  return res.data; // CourseUseResponse { course, workRecordId }
}

/**
 * 자동 완주 결과 조회
 * GET /course/completeResult?workRecordId=...
 * 반환: CourseCompleteResponse { type: "BATTLE" | "GENERAL", targetId: number }
 */
export async function getCompleteResult(workRecordId) {
  const res = await apiClient.get("/course/completeResult", {
    params: { workRecordId },
  });
  return res.data; // CourseCompleteResponse
}

/**
 * 코스 포기
 * POST /course/giveupCourse?workRecordId=...
 * 반환: 바디 없음 (void)
 */
export async function giveupCourse(workRecordId) {
  await apiClient.post("/course/giveupCourse", null, {
    params: { workRecordId },
  });
}

// 측정중(IS_MEASURE) 운동기록 조회
// - GET /course/getUsingWorkRecord?courseId=.&userId=.
// - 반환 예시: { workRecordId: 123 } 또는 { workRecordId: null }
export async function getUsingWorkRecord(courseId, userId) {
  const res = await apiClient.get("/workrecord/getMeasureWorkRecord", {
    params: { courseId, userId },
  });
  return res.data;
}

// 생성중(코스 생성 측정중) 운동기록 조회
// - 조건: IS_MEASURE=1 AND COURSE_ID IS NULL AND USER_ID=?
// - GET /workrecord/getAddingWorkRecord?userId=...
// - 반환: { workRecordId: number|null }
export async function getAddingWorkRecord(userId) {
  const res = await apiClient.get("/workrecord/getAddingWorkRecord", {
    params: { userId },
  });
  return res.data;
}

// 후기 삭제
export async function deleteReviewApi(courseId, reviewId, userId) {
  await apiClient.delete("/course/deleteReview", {
    params: {
      courseId,
      reviewId,
      userId,
    },
  });
}

// 후기 등록
// - POST /course/addReview?courseId=...&userId=...
// - body 에는 Review(내용, 별점, 이미지 등) 구조로 전달
export async function addReviewApi(courseId, userId, review) {
  const res = await apiClient.post(
    "/course/addReview",
    review, // { reviewContent, reviewRating, reviewImages: [ { image: "..." }, ... ] }
    {
      params: {
        courseId,
        userId,
      },
    }
  );
  return res.data; // 생성된 Review
}

// 후기 이미지 업로드
// - POST /course/uploadReviewImage?courseId=...
// - body: multipart/form-data (image 파일 1개)
export async function uploadReviewImageApi(courseId, file) {
  const formData = new FormData();
  formData.append("image", file);

  const res = await apiClient.post("/course/uploadReviewImage", formData, {
    params: {
      courseId,
    },
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  // 서버에서 { imageUrl: "저장된_이미지_경로" } 형태로 내려준다고 가정
  return res.data.imageUrl;
}

// 후기 수정
// - PUT /course/updateReview?courseId=...&reviewId=...&userId=...
export async function updateReviewApi(courseId, reviewId, userId, review) {
  await apiClient.put("/course/updateReview", review, {
    params: {
      courseId,
      reviewId,
      userId,
    },
  });
}

// 후기 작성 사전 권한 체크
export async function checkAddReviewPermission(courseId, userId) {
  const res = await apiClient.get("/course/checkAddReviewPermission", {
    params: { courseId, userId },
  });
  return res.data; // ReviewPermissionResponse
}

// 후기 수정 사전 권한 체크
export async function checkUpdateReviewPermission(courseId, reviewId, userId) {
  const res = await apiClient.get("/course/checkUpdateReviewPermission", {
    params: { courseId, reviewId, userId },
  });
  return res.data; // ReviewPermissionResponse
}

// 코스 생성 시작 (운동 기록 시작)
/**
 * POST /course/addingCourse
 * params: userId, type
 * 반환: workRecordId (int)
 */
export async function startAddingCourse(userId, type) {
  const res = await apiClient.post(
    "/course/addingCourse",
    null, // body 없음, 쿼리스트링/파라미터만 사용
    {
      params: { userId, type },
    }
  );
  return res.data; // int workRecordId
}

/**
 * 코스 생성 완료
 * POST /course/endCourse
 * params: userId, workRecordId, courseName, type
 * 반환: 생성된 Course 도메인
 */
export async function endCourseApi(
  userId,
  workRecordId,
  courseName,
  type,
  location
) {
  const res = await apiClient.post("/course/endCourse", null, {
    params: {
      userId,
      workRecordId,
      courseName,
      type,
      location,
    },
  });
  return res.data;
}

export async function listCoursePathPoints(courseId) {
  const res = await apiClient.get("/course/listCoursePathPoints", {
    params: { courseId },
  });
  return res.data; // [{lat, lng}, ...]
}

/**
 * 마지막 측정값 조회
 * GET /api/measurement/last/{workRecordId}
 * 반환: Measurement 도메인
 */
export async function getLastMeasurement(workRecordId) {
  const res = await apiClient.get(`/measurement/last/${workRecordId}`);
  return res.data; // Measurement
}
